// Voice input with the phone's own speech recognition (Owner decision list 2026-10-19 item 32: 「用手机自带
// 语音识别」). Where the web layer exposes the system recogniser (Safari / Chrome; the iPhone app's web view
// where iOS offers it) the mic button listens directly; everywhere else the button puts the cursor in the
// field and the inspector taps the keyboard's microphone — the same built-in recogniser. Nothing is sent
// to any server of ours.
import { Capacitor, registerPlugin } from '@capacitor/core';

export type SpeechLang = 'en-US' | 'zh-CN';

export interface SpeechHandlers {
  onText: (text: string) => void;
  onEnd: () => void;
  // 'unavailable': this phone has no recogniser after all — the keyboard's microphone is the way.
  onError: (code: 'denied' | 'noSpeech' | 'failed' | 'unavailable') => void;
}

export interface SpeechSession {
  stop: () => void;
}

export interface SpeechAdapter {
  available: () => boolean;
  start: (lang: SpeechLang, handlers: SpeechHandlers) => SpeechSession | null;
}

interface RecognitionResultEvent {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
}

interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  start: () => void;
  stop: () => void;
}

type RecognitionCtor = new () => Recognition;

function ctor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const webSpeechAdapter: SpeechAdapter = {
  available: () => ctor() !== null,
  start(lang, handlers) {
    const Ctor = ctor();
    if (!Ctor) return null;
    let recognition: Recognition;
    try {
      recognition = new Ctor();
    } catch {
      return null;
    }
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          const text = result[0].transcript.trim();
          if (text.length > 0) handlers.onText(text);
        }
      }
    };
    recognition.onerror = (event) => handlers.onError(event.error === 'not-allowed' || event.error === 'service-not-allowed' ? 'denied' : event.error === 'no-speech' ? 'noSpeech' : 'failed');
    recognition.onend = () => handlers.onEnd();
    try {
      recognition.start();
    } catch {
      return null;
    }
    return { stop: () => recognition.stop() };
  }
};

// Android (acceptance 2026-10-21, D-15): the WebView exposes no working recogniser — 说话 stuck on
// 「正在听」 behind a permission error. The app's own plugin (android/…/SpeechInputPlugin.java) opens the
// phone's voice typing screen, which owns the microphone, and hands back one sentence. Stopping is done on
// that screen, so `stop` has nothing to do here.
interface SpeechInputPlugin {
  recognize(options: { lang: SpeechLang }): Promise<{ status: 'ok' | 'cancelled'; text: string }>;
}

const SpeechInput = registerPlugin<SpeechInputPlugin>('SpeechInput');

export const androidSpeechAdapter: SpeechAdapter = {
  available: () => Capacitor.getPlatform() === 'android' && Capacitor.isPluginAvailable('SpeechInput'),
  start(lang, handlers) {
    let ended = false;
    const end = () => {
      if (ended) return;
      ended = true;
      handlers.onEnd();
    };
    SpeechInput.recognize({ lang })
      .then((result) => {
        const text = result.status === 'ok' ? result.text.trim() : '';
        if (text.length > 0) handlers.onText(text);
        end();
      })
      .catch((raw: unknown) => {
        const code = typeof raw === 'object' && raw !== null && 'code' in raw ? String((raw as { code?: unknown }).code) : '';
        handlers.onError(code === 'UNAVAILABLE' ? 'unavailable' : 'failed');
        end();
      });
    return { stop: () => undefined };
  }
};

let override: SpeechAdapter | null = null;

export function setSpeechAdapterForTests(adapter: SpeechAdapter | null): void {
  override = adapter;
}

export function getSpeechAdapter(): SpeechAdapter {
  if (override) return override;
  return androidSpeechAdapter.available() ? androidSpeechAdapter : webSpeechAdapter;
}

// How dictated text joins what is already in the field: Chinese runs on, English gets a space.
export function appendDictation(current: string, addition: string, lang: SpeechLang): string {
  const add = addition.trim();
  if (add.length === 0) return current;
  if (current.trim().length === 0) return lang === 'en-US' ? add.charAt(0).toUpperCase() + add.slice(1) : add;
  const separator = lang === 'zh-CN' || /\s$/.test(current) ? '' : ' ';
  return `${current}${separator}${add}`;
}
